<template>
  <ListComponent title="Emergency Order Containers" :totalCount="totalItems">
    <template #actions>
      <!--
      <v-menu offset-y>
        <template #activator="{ on, attrs }">
          <v-btn color="secondary" outlined small class="mr-2" v-bind="attrs" v-on="on">
            <v-icon left small>mdi-flask-outline</v-icon>
            Simulate
            <v-icon right small>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item link @click="openSimulateDialog('internal')">
            <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-account-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title class="text-body-2">Internal Flow</v-list-item-title></v-list-item-content>
          </v-list-item>
          <v-list-item link @click="openSimulateDialog('vendor')">
            <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-truck-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title class="text-body-2">Vendor Flow</v-list-item-title></v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      -->

      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn icon color="primary" class="mr-2" v-bind="attrs" v-on="on" @click="openChartDialog">
            <v-icon>mdi-chart-bar</v-icon>
          </v-btn>
        </template>
        <span>View Charts</span>
      </v-tooltip>
      <v-btn color="primary" outlined small @click="openExportDialog">
        <v-icon left small>mdi-file-export-outline</v-icon>
        Export
      </v-btn>
    </template>
    <template #list>
      <v-row class="ma-0">
        <v-col cols="12" md="4" class="pl-0 pt-0 pb-2 d-flex align-center" style="gap:8px">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            style="min-width:150px"
            v-model="search"
            @click:clear="onSearchClear"
            @keyup.enter="onSearch"
          />

          <!-- Container No. multi-search chip (comma / space / newline separated) -->
          <template v-if="!showContainerChip">
            <v-chip
              small
              color="orange darken-2"
              dark
              style="cursor:pointer; white-space:nowrap; max-width:180px; flex:0 0 auto"
              @click="showContainerChip = true"
            >
              <v-icon left x-small>mdi-magnify</v-icon>
              <span style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap">
                {{ containerChipInput || 'Container No.' }}
              </span>
              <v-icon
                v-if="containerChipInput"
                right
                x-small
                @click.stop="clearContainerChip"
              >mdi-close</v-icon>
            </v-chip>
          </template>
          <template v-else>
            <v-text-field
              v-model="containerChipInput"
              dense
              outlined
              hide-details
              placeholder="Container nos (comma separated)"
              style="min-width:260px; flex:1 1 auto"
              autofocus
              append-icon="mdi-close"
              @click:append="clearContainerChip"
              @keyup.enter="applyContainerChipSearch"
            />
          </template>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFiltersDialog = true"
                class="mx-2"
              >
                <v-badge dot color="primary" :value="appliedFilterCount">
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span>Filters</span>
          </v-tooltip>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-chip
                small
                :color="filters.no_gate_out ? 'amber darken-2' : undefined"
                :outlined="!filters.no_gate_out"
                :dark="!!filters.no_gate_out"
                style="cursor:pointer; white-space:nowrap; flex:0 0 auto"
                v-bind="attrs"
                v-on="on"
                @click="toggleNoGateOutFilter"
              >
                <v-icon left x-small>mdi-alert</v-icon>
                No Gate Out
              </v-chip>
            </template>
            <span>Show only containers with a gate in request but no gate out</span>
          </v-tooltip>
        </v-col>
        <v-col cols="6" md="1" class="pt-0 pb-2">
          <MultiSelect
            label="Status"
            :items="statusList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.status"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="2" class="pt-0 pb-2">
          <DateField
            :inputProps="{
              dense: true,
              label: 'Movement Date',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="filters.movement_date"
            @input="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="1" class="pt-0 pb-2">
          <MultiSelect
            label="Sub Hire"
            :items="subHireList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.is_subhire"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="2" class="pt-0 pb-2">
          <MultiSelect
            label="Job Type"
            :items="jobTypeList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.job_type"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="12" md="2" class="pr-0 pt-0 pb-2 d-flex align-center justify-end">
          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
        <EmergencyOrderContainerFilters
          v-model="showFiltersDialog"
          :bindingObject="filters"
          :originList="originList"
          :originCount="originCount"
          :originLoading="originLoading"
          :destinationList="destinationList"
          :destinationCount="destinationCount"
          :destinationLoading="destinationLoading"
          :selectedOriginObj="selectedOriginObj"
          :selectedDestinationObj="selectedDestinationObj"
          :vendorList="vendorList"
          :vendorCount="vendorCount"
          :vendorLoading="vendorLoading"
          :selectedVendorObj="selectedVendorObj"
          @update:selectedOriginObj="onOriginSelected"
          @update:selectedDestinationObj="onDestinationSelected"
          @update:selectedVendorObj="onVendorSelected"
          @getOriginList="getOriginList"
          @getDestinationList="getDestinationList"
          @getVendorList="getVendorList"
          @applyFilters="onFilterChange"
          @resetFilters="resetFilters"
        />
        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 190px)"
            class="ag-theme-alpine"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :rowData="allList"
          />
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <SimulateGateEventDialog
        v-model="showSimulateDialog"
        :flow="simulateFlow"
        :prefillContainer="simulatePrefillContainer"
        @simulated="onFilterChange"
      />

      <EmergencyOrderContainerChartDialog
        v-model="showChartDialog"
        :params="chartParams"
      />

      <v-dialog max-width="440px" v-model="showExportDialog" persistent>
        <v-card>
          <v-card-title class="secondary white--text py-3 px-5">
            <span class="text-subtitle-1 font-weight-bold">Export Emergency Order Containers</span>
            <v-spacer />
            <v-btn icon color="white" small @click="showExportDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-5 pt-4">
            <div class="caption grey--text mb-3">
              Leave both dates blank to export all records matching the current filters.
            </div>
            <v-row dense>
              <v-col cols="6">
                <DateField
                  :inputProps="{ dense: true, outlined: true, label: 'Movement Date From', 'hide-details': 'auto', clearable: true }"
                  v-model="exportRange.from"
                />
              </v-col>
              <v-col cols="6">
                <DateField
                  :inputProps="{ dense: true, outlined: true, label: 'Movement Date To', 'hide-details': 'auto', clearable: true }"
                  v-model="exportRange.to"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="mt-3" />
          <v-card-actions class="px-5 py-3">
            <v-spacer />
            <v-btn depressed outlined @click="showExportDialog = false" class="mr-2">Cancel</v-btn>
            <v-btn color="primary" depressed :loading="exporting" min-width="120" @click="exportToExcel">
              <v-icon small class="mr-1">mdi-download</v-icon>
              Export
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </ListComponent>
</template>
<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import Pagination from "@/components/General/Pagination.vue";
import EmergencyOrderContainerFilters from "@/components/ModuleBased/dialogs/filters/EmergencyOrderContainerFilters.vue";
import EmergencyOrderContainerViewButton from "@/components/AgGridComponents/EmergencyOrderContainerViewButton.vue";
import EmergencyOrderContainerStatusChip from "@/components/AgGridComponents/EmergencyOrderContainerStatusChip.vue";
import SimulateGateInRowButton from "@/components/AgGridComponents/SimulateGateInRowButton.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import SimulateGateEventDialog from "@/components/ModuleBased/dialogForms/SimulateGateEventDialog.vue";
import EmergencyOrderContainerChartDialog from "@/components/ModuleBased/dialogs/EmergencyOrderContainerChartDialog.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    agColumnHeader: CustomHeader,
    AgGridVue,
    Pagination,
    EmergencyOrderContainerFilters,
    EmergencyOrderContainerViewButton,
    EmergencyOrderContainerStatusChip,
    SimulateGateInRowButton,
    MultiSelect,
    DateField,
    SimulateGateEventDialog,
    EmergencyOrderContainerChartDialog,
  },
  data() {
    return {
      allList: [],
      totalItems: 0,
      search: "",
      containerChipInput: "",
      showContainerChip: false,
      showFiltersDialog: false,
      filterOptionsLoaded: false,
      simulatePrefillContainer: null,
      simulateFlow: "internal",
      showSimulateDialog: false,
      filters: {
        status: null,
        origin: null,
        destination: null,
        vendor: null,
        movement_date: null,
        completed_on: null,
        subhire_created_date: null,
        is_subhire: null,
        failure_reason: null,
        job_type: null,
        no_gate_out: null,
      },
      statusList: [
        { text: "Pending", value: "Pending" },
        { text: "Active", value: "Active" },
        { text: "Completed", value: "Completed" },
        { text: "Cancelled", value: "Cancelled" },
      ],
      subHireList: [
        { text: "Yes", value: "Yes" },
        { text: "No", value: "No" },
      ],
      jobTypeList: [
        { text: "Import", value: "Import" },
        { text: "Export", value: "Export" },
        { text: "FCL", value: "FCL" },
        { text: "LCL", value: "LCL" },
        { text: "Empty", value: "Empty" },
      ],
      originList: [],
      originCount: 0,
      originLoading: false,
      selectedOriginObj: null,
      destinationList: [],
      destinationCount: 0,
      destinationLoading: false,
      selectedDestinationObj: null,
      vendorList: [],
      vendorCount: 0,
      vendorLoading: false,
      selectedVendorObj: null,
      showExportDialog: false,
      showChartDialog: false,
      chartParams: {},
      exportRange: { from: "", to: "" },
      exporting: false,
      pageNo: 1,
      itemsPerPage: 50,
      permissions: null,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.gridOptions.api.sizeColumnsToFit(),
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "VBS Ref No",
          field: "vbs_reference_no",
          sortable: true,
          minWidth: 150,
          cellClass: ["primary--text font-weight-bold cursor-pointer"],
          onCellClicked: (params) => this.openContainerDetails(params.data),
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 120,
          cellRendererFramework: "EmergencyOrderContainerStatusChip",
        },
        {
          headerName: "Job Type",
          field: "job_type",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Container No",
          field: "container_number",
          sortable: true,
          minWidth: 170,
          cellRenderer: (params) => {
            const value = params.value || "";
            if (!params.data || !params.data.no_gate_out) {
              return value;
            }
            return `<span style="display:inline-flex;align-items:center;">
                <span>${value}</span>
                <span
                  title="No gate out request received for this container — it was created directly from the gate in event."
                  style="display:inline-flex;align-items:center;justify-content:center;margin-left:6px;width:18px;height:18px;border-radius:50%;background:#fef3c7;flex-shrink:0;cursor:default;"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L1 21h22L12 2z" fill="#b45309"/>
                    <rect x="11" y="9" width="2" height="6" fill="#ffffff"/>
                    <rect x="11" y="16" width="2" height="2" fill="#ffffff"/>
                  </svg>
                </span>
              </span>`;
          },
        },
        {
          headerName: "Container Type",
          field: "container_type_name",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Origin",
          field: "origin_name",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Destination",
          field: "destination_name",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Vendor",
          field: "vendor_name",
          sortable: true,
          minWidth: 180,
        },
        {
          headerName: "Trip ID",
          field: "trip_id",
          sortable: true,
          minWidth: 150,
          cellClass: (params) =>
            params.data && params.data.trip
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openTripDetails(params.data),
        },
        {
          headerName: "DA No",
          field: "da_no",
          sortable: true,
          minWidth: 150,
          cellClass: (params) =>
            params.data && params.data.da
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openDaDetails(params.data),
        },
        {
          headerName: "Sub Hire ID",
          field: "subhire_job_no",
          sortable: true,
          minWidth: 150,
          valueGetter: (params) =>
            (params.data && params.data.subhire_job_no) || "",
          cellClass: (params) =>
            params.data && params.data.subhire_job
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openSubHireDetails(params.data),
        },
        {
          headerName: "Sub Hire Creation Date",
          field: "subhire_job_created",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value) : "-",
        },
        {
          headerName: "Vehicle No",
          field: "vehicle_no",
          sortable: true,
          minWidth: 140,
        },
        {
          headerName: "Driver",
          field: "driver_name",
          sortable: true,
          minWidth: 140,
          valueGetter: (params) =>
            (params.data &&
              (params.data.driver_name || params.data.driver_id_raw)) ||
            "-",
        },
        {
          headerName: "Codeco Gate Out Driver",
          field: "codeco_gate_out_driver",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Codeco Gate Out Vehicle",
          field: "codeco_gate_out_vehicle",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Codeco Gate In Driver",
          field: "codeco_gate_in_driver",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Codeco Gate In Vehicle",
          field: "codeco_gate_in_vehicle",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trip Driver",
          field: "trip_driver_name",
          sortable: false,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trip Vehicle",
          field: "trip_vehicle_number",
          sortable: false,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Movement Date",
          field: "movement_date",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value) : "-",
        },
        {
          headerName: "Sub Hire",
          field: "is_subhire",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) => (params.value ? "Yes" : "No"),
        },
        {
          headerName: "DA Rate",
          field: "rate",
          sortable: true,
          minWidth: 130,
          valueFormatter: (params) =>
            params.value ? `AED ${params.value}` : "-",
        },
        {
          headerName: "Sub Hire Rate",
          field: "subhire_rate",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) =>
            params.value ? `AED ${params.value}` : "-",
        },
        {
          headerName: "Gate Out Timestamp",
          field: "pickup_departure",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Gate In Timestamp",
          field: "drop_arrival",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Trip Trailer 1",
          field: "trip_trailer_1",
          sortable: false,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trip Trailer 2",
          field: "trip_trailer_2",
          sortable: false,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trip Date",
          field: "trip_date",
          sortable: false,
          minWidth: 140,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value) : "-",
        },
        {
          headerName: "Trip Start",
          field: "trip_start",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Trip Completion Date",
          field: "trip_end",
          sortable: false,
          minWidth: 180,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Trip Completion Agent",
          field: "trip_completion_agent",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trip Completed By",
          field: "trip_completed_by",
          sortable: false,
          minWidth: 160,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trip Creation",
          field: "trip_created",
          sortable: false,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Trip Created By",
          field: "trip_created_by",
          sortable: false,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Created",
          field: "created",
          sortable: true,
          minWidth: 160,
        },
        {
          headerName: "Failure Reason",
          field: "failure_reason",
          sortable: false,
          minWidth: 260,
          tooltipValueGetter: (params) =>
            params.value ? params.value.split("\n").join(", ") : "",
          valueFormatter: (params) =>
            params.value ? params.value.split("\n").join(", ") : "-",
          cellClass: (params) =>
            params.value ? "red--text" : "",
        },
        /*
        {
          headerName: "Simulate Gate In",
          field: "simulate_gate_in",
          cellRendererFramework: "SimulateGateInRowButton",
          pinned: "right",
          minWidth: 130,
          width: 130,
          suppressSizeToFit: true,
        },
        */
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "EmergencyOrderContainerViewButton",
          pinned: "right",
          minWidth: 90,
          width: 90,
          suppressSizeToFit: true,
        },
      ],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    appliedFilterCount() {
      return Object.values(this.filters).filter(
        (value) => value !== null && value !== "",
      ).length;
    },
  },
  watch: {
    showFiltersDialog(isOpen) {
      if (isOpen && !this.filterOptionsLoaded) {
        this.filterOptionsLoaded = true;
        this.getOriginList();
        this.getDestinationList();
        this.getVendorList();
      }
    },
  },
  methods: {
    onOriginSelected(value) {
      this.selectedOriginObj = value;
      this.filters.origin = value ? value.id : null;
    },
    onDestinationSelected(value) {
      this.selectedDestinationObj = value;
      this.filters.destination = value ? value.id : null;
    },
    onVendorSelected(value) {
      this.selectedVendorObj = value;
      this.filters.vendor = value ? value.id : null;
    },
    resetFilters() {
      this.filters = {
        status: null,
        origin: null,
        destination: null,
        vendor: null,
        movement_date: null,
        completed_on: null,
        subhire_created_date: null,
        is_subhire: null,
        failure_reason: null,
        job_type: null,
        no_gate_out: null,
      };
      this.selectedOriginObj = null;
      this.selectedDestinationObj = null;
      this.selectedVendorObj = null;
      this.search = "";
      this.containerChipInput = "";
      this.showContainerChip = false;
      localStorage.removeItem("emergency_order_container_state");
      this.onFilterChange();
    },
    openContainerDetails(row) {
      if (row && row.id) {
        this.$router.push({
          path: `/app/admin/emergency-order-container/details/${row.id}`,
          query: {},
        });
      }
    },
    openSimulateGateIn(row) {
      this.simulatePrefillContainer = row;
      this.simulateFlow = row.is_subhire ? "vendor" : "internal";
      this.showSimulateDialog = true;
    },
    openSimulateDialog(flow) {
      this.simulatePrefillContainer = null;
      this.simulateFlow = flow;
      this.showSimulateDialog = true;
    },
    openTripDetails(row) {
      if (row && row.trip) {
        this.$router.push({
          path: `/app/admin/draft-trip/details/${row.trip}`,
          query: {},
        });
      }
    },
    openDaDetails(row) {
      if (row && row.da) {
        this.$router.push({
          path: `/app/admin/delivery-advice/details/${row.da}`,
          query: {},
        });
      }
    },
    openSubHireDetails(row) {
      if (row && row.subhire_job) {
        this.$router.push({
          path: `/app/admin/sub-hire/details/${row.subhire_job}`,
          query: {},
        });
      }
    },
    onSearch() {
      this.pageNo = 1;
      this.getList();
    },
    onSearchClear() {
      this.search = "";
      this.pageNo = 1;
      this.getList();
    },
    onFilterChange() {
      this.pageNo = 1;
      this.getList();
    },
    toggleNoGateOutFilter() {
      this.filters.no_gate_out = this.filters.no_gate_out ? null : true;
      this.onFilterChange();
    },
    applyContainerChipSearch() {
      const normalized = (this.containerChipInput || "")
        .trim()
        .replace(/[\r\n]+/g, ",")
        .replace(/,+/g, ",")
        .replace(/^,|,$/g, "");
      this.containerChipInput = normalized;
      this.pageNo = 1;
      this.getList();
    },
    clearContainerChip() {
      this.containerChipInput = "";
      this.showContainerChip = false;
      this.pageNo = 1;
      this.getList();
    },
    persistState() {
      const { depot, branch, ...filtersToSave } = this.filters;
      localStorage.setItem(
        "emergency_order_container_state",
        JSON.stringify({
          filters: filtersToSave,
          search: this.search,
          containerChipInput: this.containerChipInput,
          itemsPerPage: this.itemsPerPage,
          selectedOriginObj: this.selectedOriginObj,
          selectedDestinationObj: this.selectedDestinationObj,
          selectedVendorObj: this.selectedVendorObj,
        }),
      );
    },
    restoreState() {
      const raw = localStorage.getItem("emergency_order_container_state");
      if (!raw) return;
      try {
        const s = JSON.parse(raw);
        if (s.filters) this.filters = { ...this.filters, ...s.filters };
        if (typeof s.search === "string") this.search = s.search;
        if (typeof s.containerChipInput === "string") {
          this.containerChipInput = s.containerChipInput;
          this.showContainerChip = false;
        }
        if (s.itemsPerPage) this.itemsPerPage = s.itemsPerPage;
        if (s.selectedOriginObj) this.selectedOriginObj = s.selectedOriginObj;
        if (s.selectedDestinationObj) this.selectedDestinationObj = s.selectedDestinationObj;
        if (s.selectedVendorObj) this.selectedVendorObj = s.selectedVendorObj;
      } catch (e) {
        console.error("Failed to restore emergency order container state", e);
      }
    },
    getOriginList(params = {}) {
      this.originLoading = true;
      params = { offset: 0, limit: 10, ...params };
      this.$api.location
        .getLocationList(params)
        .then((res) => {
          this.originLoading = false;
          if ("count" in res) {
            this.originCount = res.count;
          }
          if (params.offset == 0) {
            this.originList = res.data;
          } else {
            this.originList.push(...res.data);
          }
        })
        .catch(() => {
          this.originLoading = false;
        });
    },
    getDestinationList(params = {}) {
      this.destinationLoading = true;
      params = { offset: 0, limit: 10, ...params };
      this.$api.location
        .getLocationList(params)
        .then((res) => {
          this.destinationLoading = false;
          if ("count" in res) {
            this.destinationCount = res.count;
          }
          if (params.offset == 0) {
            this.destinationList = res.data;
          } else {
            this.destinationList.push(...res.data);
          }
        })
        .catch(() => {
          this.destinationLoading = false;
        });
    },
    getVendorList(params = {}) {
      this.vendorLoading = true;
      params = { offset: 0, limit: 10, company_type: "Transporter", ...params };
      this.$api.company
        .getCompanyList(params)
        .then((res) => {
          this.vendorLoading = false;
          if ("count" in res) {
            this.vendorCount = res.count;
          }
          if (params.offset == 0) {
            this.vendorList = res.data;
          } else {
            this.vendorList.push(...res.data);
          }
        })
        .catch(() => {
          this.vendorLoading = false;
        });
    },
    openExportDialog() {
      this.exportRange = { from: "", to: "" };
      this.showExportDialog = true;
    },
    openChartDialog() {
      const params = {};
      if (this.search) {
        params.search = this.search;
      }
      if (this.containerChipInput && this.containerChipInput.trim()) {
        params.container_numbers = this.containerChipInput.trim();
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.origin) {
        params.origin = this.filters.origin;
      }
      if (this.filters.destination) {
        params.destination = this.filters.destination;
      }
      if (this.filters.vendor) {
        params.vendor = this.filters.vendor;
      }
      if (this.filters.movement_date) {
        params.movement_date = this.filters.movement_date;
      }
      if (this.filters.completed_on) {
        params.completed_on = this.filters.completed_on;
      }
      if (this.filters.subhire_created_date) {
        params.subhire_created_date = this.filters.subhire_created_date;
      }
      if (this.filters.is_subhire) {
        params.is_subhire = this.filters.is_subhire;
      }
      if (this.filters.failure_reason) {
        params.failure_reason = this.filters.failure_reason;
      }
      if (this.filters.job_type) {
        params.job_type = this.filters.job_type;
      }
      if (this.filters.no_gate_out) {
        params.no_gate_out = this.filters.no_gate_out;
      }
      this.chartParams = params;
      this.showChartDialog = true;
    },
    exportToExcel() {
      this.exporting = true;
      const params = {};
      if (this.search) {
        params.search = this.search;
      }
      if (this.containerChipInput && this.containerChipInput.trim()) {
        params.container_numbers = this.containerChipInput.trim();
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.origin) {
        params.origin = this.filters.origin;
      }
      if (this.filters.destination) {
        params.destination = this.filters.destination;
      }
      if (this.filters.vendor) {
        params.vendor = this.filters.vendor;
      }
      if (this.exportRange.from) {
        params.movement_date_from = this.exportRange.from;
      }
      if (this.exportRange.to) {
        params.movement_date_to = this.exportRange.to;
      }
      if (!this.exportRange.from && !this.exportRange.to && this.filters.movement_date) {
        params.movement_date = this.filters.movement_date;
      }
      if (this.filters.completed_on) {
        params.completed_on = this.filters.completed_on;
      }
      if (this.filters.subhire_created_date) {
        params.subhire_created_date = this.filters.subhire_created_date;
      }
      if (this.filters.is_subhire) {
        params.is_subhire = this.filters.is_subhire;
      }
      if (this.filters.failure_reason) {
        params.failure_reason = this.filters.failure_reason;
      }
      if (this.filters.job_type) {
        params.job_type = this.filters.job_type;
      }
      if (this.filters.no_gate_out) {
        params.no_gate_out = this.filters.no_gate_out;
      }
      this.$api.emergencyOrderContainer
        .exportEmergencyOrderContainers(params)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Emergency_Order_Containers.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();
          this.exporting = false;
          this.showExportDialog = false;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "No records found to export.",
            color: "error",
          });
          this.exporting = false;
        });
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getList();
    },
    prevPage() {
      this.pageNo--;
      this.getList();
    },
    nextPage() {
      this.pageNo++;
      this.getList();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    getList() {
      this.persistState();
      this.$bus.$emit("showLoader", true);
      const params = {
        offset: this.offset,
        limit: this.itemsPerPage,
      };
      if (this.search) {
        params.search = this.search;
      }
      if (this.containerChipInput && this.containerChipInput.trim()) {
        params.container_numbers = this.containerChipInput.trim();
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.origin) {
        params.origin = this.filters.origin;
      }
      if (this.filters.destination) {
        params.destination = this.filters.destination;
      }
      if (this.filters.vendor) {
        params.vendor = this.filters.vendor;
      }
      if (this.filters.movement_date) {
        params.movement_date = this.filters.movement_date;
      }
      if (this.filters.completed_on) {
        params.completed_on = this.filters.completed_on;
      }
      if (this.filters.subhire_created_date) {
        params.subhire_created_date = this.filters.subhire_created_date;
      }
      if (this.filters.is_subhire) {
        params.is_subhire = this.filters.is_subhire;
      }
      if (this.filters.failure_reason) {
        params.failure_reason = this.filters.failure_reason;
      }
      if (this.filters.job_type) {
        params.job_type = this.filters.job_type;
      }
      if (this.filters.no_gate_out) {
        params.no_gate_out = this.filters.no_gate_out;
      }
      this.$api.emergencyOrderContainer
        .getEmergencyOrderContainerList(params)
        .then((res) => {
          this.allList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.restoreState();
        if (this.$route.query.search) {
          this.search = this.$route.query.search;
        }
        this.getList();
      }
    }
  },
};
</script>
